import { get, isNil } from "lodash";
import {
  MdAdd,
  MdClose,
  MdOutlineLayers,
  MdWarningAmber,
} from "react-icons/md";
import format from "string-template";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import InfoBox from "../Components/InfoBox";
import { OPTION_RANKS } from "../constants";
import { compositionErrorMessage, isOptionAddDisabled } from "../helpers";

const Configurator = ({
  translation: T,
  levelPlans,
  upsellPlans,
  offeredUpsellIds,
  catalogLoading,
  combinations,
  compositionErrors,
  onPlanChange,
  onUpsellToggle,
  onOptionAdd,
  onOptionRemove,
  disabled,
}) => {
  const ranks = combinations.map(({ id }) => id);
  const addableRank = OPTION_RANKS.find((rank) => !ranks.includes(rank));
  const addPreviousRank = isNil(addableRank) ? null : ranks[ranks.length - 1];
  const addDisabled = isOptionAddDisabled({
    disabled,
    addableRank,
    combinations,
  });

  return (
    <div className="ac-collectif__configurator">
      <div className="ac-collectif__configurator-offers">
        {combinations.map((combination, index) => {
          const rank = combination.id;
          const planId = combination.baseInsurancePlanId;
          const previousLevel = get(
            levelPlans.find(
              ({ id }) =>
                id === get(combinations, [index - 1, "baseInsurancePlanId"]),
            ),
            "level",
          );
          const selectedUpsellIds = combination.upsellInsurancePlanIds || [];
          const offered = get(offeredUpsellIds, planId, []);

          return (
            <div className="ac-collectif__column" key={rank}>
              <div className="ac-collectif__column-head">
                <MdOutlineLayers size={20} />
                <h4 className="ac-collectif__column-title">{T.RANKS[rank]}</h4>
                {index > 0 && (
                  <button
                    type="button"
                    className="ac-collectif__column-remove"
                    disabled={disabled}
                    aria-label={format(T.OFFER.CONFIGURATOR.REMOVE, {
                      rank: T.RANKS[rank],
                    })}
                    title={format(T.OFFER.CONFIGURATOR.REMOVE, {
                      rank: T.RANKS[rank],
                    })}
                    onClick={() => onOptionRemove(rank)}
                  >
                    <MdClose size={16} />
                  </button>
                )}
              </div>
              <span className="ac-collectif__column-badge">
                {index === 0
                  ? T.OFFER.CONFIGURATOR.MANDATORY
                  : T.OFFER.CONFIGURATOR.OPTIONAL}
              </span>

              <Select
                allowClear={index > 0}
                disabled={disabled || catalogLoading}
                required={index === 0}
                options={levelPlans
                  .filter(
                    ({ level }) =>
                      isNil(previousLevel) || level >= previousLevel,
                  )
                  .map(({ id, level }) => ({
                    value: id,
                    label: format(T.LEVEL, { level }),
                  }))}
                label={T.OFFER.CONFIGURATOR.LEVEL.LABEL}
                placeholder={T.OFFER.CONFIGURATOR.LEVEL.PLACEHOLDER}
                value={planId}
                onChange={(value) => onPlanChange(rank, value ?? null)}
                name={`combinations.${rank}.baseInsurancePlanId`}
              />

              <div className="ac-collectif__reinforcements">
                <span className="ac-collectif__reinforcements-title">
                  {T.OFFER.CONFIGURATOR.REINFORCEMENTS.TITLE}
                </span>
                {upsellPlans.map((upsell) => (
                  <Input
                    key={upsell.id}
                    type="checkbox"
                    id={`${rank}-${upsell.id}`}
                    name={`combinations.${rank}.upsellInsurancePlanIds.${upsell.id}`}
                    label={upsell.name}
                    checked={selectedUpsellIds.includes(upsell.id)}
                    disabled={disabled || !offered.includes(upsell.id)}
                    onChange={({ target: { checked } }) =>
                      onUpsellToggle(rank, upsell.id, checked)
                    }
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {!isNil(addableRank) && (
        <button
          type="button"
          className="ac-collectif__add-offer"
          disabled={addDisabled}
          title={
            addDisabled
              ? format(T.OFFER.CONFIGURATOR.ADD_DISABLED, {
                  rank: T.RANKS[addPreviousRank],
                })
              : undefined
          }
          onClick={() => onOptionAdd(addableRank)}
        >
          <MdAdd size={18} />
          {format(T.OFFER.CONFIGURATOR.ADD, { rank: T.RANKS[addableRank] })}
        </button>
      )}

      <p className="ac-collectif__reinforcements-hint">
        {T.OFFER.CONFIGURATOR.REINFORCEMENTS.HINT}
      </p>

      {compositionErrors.length > 0 && (
        <InfoBox
          variant="danger"
          className="ac-collectif__composition-errors"
          icon={<MdWarningAmber size={24} />}
          title={T.OFFER.CONFIGURATOR.BLOCKING_TITLE}
          description={
            <ul>
              {compositionErrors.map((error) => (
                <li key={`${error.key}-${JSON.stringify(error.params)}`}>
                  {compositionErrorMessage(T, error)}
                </li>
              ))}
            </ul>
          }
        />
      )}
    </div>
  );
};

export default Configurator;
